import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { EntityPath, encodeFieldKey } from '@/schemas/validation/ValidationSchemaGenerator';
import { useSceneStore, useSceneField } from '@/stores';
import { SceneDataElement } from '@/schemas/sceneDataSchema';
import { persistStep } from '@/api/agency-quote-api';
import { applyPersistData } from '@/stores/sync';
import { reportError } from '@/services/error-reporting-service';
import { getRouteStep } from '@/services/location-service';

// value is | undefined (vs SceneDataElement['value']) because the field may not yet be hydrated in the store.
type FieldBinding = Pick<SceneDataElement, 'valid' | 'message' | 'masked'> & {
  value: SceneDataElement['value'] | undefined;
  sync: (value: SceneDataElement['value']) => void;
  // Persist the current pending changes to the server if persist_on_blur is enabled.
  // No-ops if the field has no pending change or if the current step isn't available in the URL.
  onBlur: () => void;
};

/**
 * Seed a react-hook-form field from the store on mount.
 * Only writes when the store value differs from the current form value.
 * Pass a `fallback` for fields that need a default (e.g. `false` for checkboxes).
 */
export function seedFormFromStore(
  form: UseFormReturn,
  key: string,
  value: SceneDataElement['value'] | undefined,
  fallback?: SceneDataElement['value']
): void {
  const seedValue = value ?? fallback;
  if (seedValue !== undefined && seedValue !== form.getValues(key)) {
    form.setValue(key, seedValue, { shouldDirty: false, shouldValidate: false });
  }
}

export function useFieldBinding(field: EntityPath & { persist_on_blur?: boolean }, form?: UseFormReturn): FieldBinding {
  const key = encodeFieldKey(field);
  const storeField = useSceneField(key);

  const sync = useCallback(
    (value: SceneDataElement['value']) => {
      useSceneStore.getState().setValue(key, value);
    },
    [key]
  );

  const onBlur = useCallback(() => {
    if (!field.persist_on_blur) return;

    const { step_id } = getRouteStep();
    if (!step_id) return;

    const pending = useSceneStore.getState().pendingChanges[key];
    if (pending === undefined) return;

    void persistStep(step_id, field.entity_index, { [key]: pending })
      .then((sceneData) => {
        if (getRouteStep().step_id === step_id) {
          applyPersistData(sceneData.step_state, [key]);
        }
        if (form) {
          const updated = useSceneStore.getState().serverState[key];
          if (updated?.valid === false && updated.message) {
            form.setError(key, { type: 'server', message: updated.message });
          } else {
            form.clearErrors(key);
          }
        }
      })
      .catch((error) => {
        reportError({ error });
      });
  }, [key, field.persist_on_blur, field.entity_index, form]);

  return {
    value: storeField?.value,
    valid: storeField?.valid ?? true,
    message: storeField?.message ?? null,
    masked: storeField?.masked ?? false,
    sync,
    onBlur,
  };
}
